import { Directive, ElementRef, HostListener, Input, OnInit, Optional } from '@angular/core';
import { NgControl } from '@angular/forms';
import { NormalizerHelper } from 'src/app/core/helpers/normalizer.helper';

/**
 * Diretiva de máscara inteligente para campos de protocolo SGR.
 *
 * Tipos suportados:
 * - 'projeto'    → Projeto pai (307/2008) ou filho (307.003/2008). Sem pad de zeros.
 * - 'protocolo'  → Evento/protocolo simples: NNNNN/AAAA (pad de 5 zeros).
 * - 'pts'        → Processo: NN.NNNNN.NNNN
 * - 'solicitacao'→ Sem máscara (delegado ao ngx-mask externo)
 * - null         → Auto-detecta o tipo a partir do conteúdo digitado
 */
@Directive({
  selector: '[appMaskProtocolo]',
})
export class MaskProtocoloDirective implements OnInit {
  @Input() tipoMascara: 'projeto' | 'protocolo' | 'pts' | 'solicitacao' | 'cpfcnpj' | null = null;

  @Input() buscaUnificada = false;
  @Input() vinculoBusca = 0;

  constructor(private el: ElementRef, @Optional() private control: NgControl) {}

  ngOnInit(): void {
    if (this.control?.value) {
      this.aplicarFormatacao(this.control.value);
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (this.tipoMascara === 'solicitacao' && !this.buscaUnificada) return;

    this.aplicarFormatacao(input.value);
  }

  /** Filtra paste para permitir apenas chars válidos por tipo */
  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    if (this.buscaUnificada) return;
    if (this.tipoMascara === 'solicitacao') return;

    const texto = event.clipboardData?.getData('text') ?? '';
    const tipo = this.tipoMascara ?? this.detectarTipo(texto);

    const regex = tipo === 'pts'
      ? /[^0-9.]/g
      : tipo === 'projeto'
        ? /[^0-9./]/g
        : tipo === 'cpfcnpj'
          ? /[^0-9]/g
          : /[^0-9/]/g;

    if (regex.test(texto)) {
      event.preventDefault();
    }
  }

  // ---------------------------------------------------------------------------
  // Lógica privada
  // ---------------------------------------------------------------------------

  private aplicarFormatacao(valor: string): void {
    if (this.buscaUnificada) {
      const input = this.el.nativeElement as HTMLInputElement;
      const cursor = input.selectionStart ?? valor.length;
      const anteriores = valor.slice(0, cursor).replace(/[^\da-zA-ZÀ-ÿ]/g, '').length;
      const formatado = MaskProtocoloDirective.formatarBusca(valor, this.tipoMascara, this.vinculoBusca);
      if (formatado === valor) return;
      input.value = formatado;
      this.control?.control?.setValue(formatado, { emitEvent: false });
      let destino = 0;
      let encontrados = 0;
      while (destino < formatado.length && encontrados < anteriores) {
        if (/[\da-zA-ZÀ-ÿ]/.test(formatado[destino])) encontrados++;
        destino++;
      }
      input.setSelectionRange(destino, destino);
      return;
    }
    const tipo = this.tipoMascara ?? this.detectarTipo(valor);
    if (!tipo) return;

    const formatado = this.formatar(valor, tipo);
    if (formatado === valor) return;

    const input = this.el.nativeElement as HTMLInputElement;
    input.value = formatado;
    this.control?.control?.setValue(formatado, { emitEvent: false });

    // Cursor ao final após reformatar
    setTimeout(() => input.setSelectionRange(formatado.length, formatado.length));
  }

  /** Busca parcial: não completa zeros nem escolhe evento antes de conhecer o formato. */
  static formatarBusca(valor: string, modo: MaskProtocoloDirective['tipoMascara'], vinculo = 0): string {
    if (/[a-zA-ZÀ-ÿ]/.test(valor)) return valor;
    const digitos = valor.replace(/\D/g, '');
    if (modo === 'cpfcnpj') return NormalizerHelper.formatCpfCnpj(digitos);
    const pts = (numero: string): string => {
      const formatado = numero.slice(0, modo === 'solicitacao' ? numero.length : 11).replace(/^(\d{2})(\d)/, '$1.$2').replace(/^(\d{2}\.\d{5})(\d)/, '$1.$2');
      return valor.endsWith('.') && [2, 7].includes(numero.length) ? formatado + '.' : formatado;
    };
    if (modo === 'solicitacao' || modo === 'pts' || vinculo === 3) return pts(digitos);
    const limpo = valor.replace(/[^\d./]/g, '');
    if (limpo.includes('/')) {
      const [numero, ...ano] = limpo.split('/');
      const [pai, ...filho] = numero.split('.');
      const sufixo = filho.length ? '.' + filho.join('').slice(0, 3) : '';
      return `${pai.slice(0, 5)}${sufixo}/${ano.join('').replace(/\D/g, '').slice(0, 4)}`;
    }
    if (vinculo === 2) return digitos.slice(0, 9).replace(/^(\d{5})(\d)/, '$1/$2');
    if (limpo.split('.').length > 2 || (limpo.indexOf('.') === 2 && vinculo !== 1)) return pts(digitos);
    // Projeto filho ainda sem ano: o ponto é uma escolha explícita do usuário.
    if (limpo.includes('.')) {
      const [pai, ...filho] = limpo.split('.');
      return `${pai.slice(0, 5)}.${filho.join('').slice(0, 3)}`;
    }
    return digitos.slice(0, 12);
  }

  /**
   * Detecta o tipo de protocolo a partir do valor digitado.
   *
   * Regra:
   *  - temBarra E temPonto  → 'projeto' (filho: 307.003/2008)
   *  - temBarra sem ponto   → 'protocolo' (evento/projeto pai)
   *  - temPonto sem barra E >9 dígitos → 'pts'
   *  - só dígitos            → 'protocolo' (ainda digitando)
   */
  private detectarTipo(valor: string): 'projeto' | 'protocolo' | 'pts' | null {
    if (!valor?.trim()) return null;

    const temBarra = valor.includes('/');
    const temPonto = valor.includes('.');
    const digits   = valor.replace(/\D/g, '');

    if (temBarra && temPonto) return 'projeto';
    if (temBarra) return 'protocolo';
    if (temPonto && digits.length > 9) return 'pts';
    if (digits.length > 0) return 'protocolo';

    return null;
  }

  /**
   * Aplica a máscara conforme o tipo.
   */
  private formatar(valor: string, tipo: 'projeto' | 'protocolo' | 'pts' | 'solicitacao' | 'cpfcnpj'): string {
    switch (tipo) {
      case 'projeto':   return this.formatarProjeto(valor);
      case 'protocolo': return this.formatarProtocolo(valor);
      case 'pts':       return this.formatarPTS(valor);
      case 'cpfcnpj':   return NormalizerHelper.formatCpfCnpj(valor);
      default:          return valor;
    }
  }

  // ---------------------------------------------------------------------------
  // Formatadores específicos
  // ---------------------------------------------------------------------------

  /**
   * Formato projeto pai:   307/2008
   * Formato projeto filho: 307.003/2008
   *
   * - Sem zeros à esquerda no número de sequência (o banco armazena sem pad)
   * - Respeita `.` e `/` já digitados pelo usuário
   * - Aceita chars: 0-9, ., /
   */
  private formatarProjeto(valor: string): string {
    let limpo = valor.replace(/[^0-9./]/g, '');
    if (!limpo) return '';

    let hasDot = limpo.includes('.');
    let hasSlash = limpo.includes('/');

    // Se o usuário continuou digitando após uma auto-inserção, os separadores podem estar errados
    if (hasSlash) {
      const parts = limpo.split('/');
      const afterSlash = parts[parts.length - 1].replace(/\D/g, '');
      if (afterSlash.length > 4) {
        hasSlash = false;
        limpo = limpo.replace('/', '');
      }
    }
    if (hasDot) {
      const parts = limpo.split('.');
      const afterDot = parts[parts.length - 1].replace(/\D/g, '');
      if (afterDot.length > 8) {
        hasDot = false;
        limpo = limpo.replace('.', '');
      }
    }

    if (!hasDot && !hasSlash) {
      const digits = limpo.replace(/\D/g, '');
      if (digits.length <= 5) return digits;
      if (digits.length <= 9) {
        return `${digits.substring(0, digits.length - 4)}/${digits.substring(digits.length - 4)}`;
      }
      const seq = digits.substring(0, digits.length - 7);
      const suffix = digits.substring(digits.length - 7, digits.length - 4);
      const year = digits.substring(digits.length - 4);
      return `${seq}.${suffix}/${year}`;
    }

    let seq = '';
    let suffix = '';
    let year = '';

    if (hasDot) {
      const partesPonto = limpo.split('.');
      seq = partesPonto[0].replace(/\D/g, '');
      const restoStr = partesPonto.slice(1).join('');
      const partesBarra = restoStr.split('/');
      const restoSemBarra = partesBarra[0].replace(/\D/g, '');
      
      suffix = restoSemBarra.substring(0, 3);
      
      if (restoStr.includes('/')) {
        year = partesBarra.slice(1).join('').replace(/\D/g, '');
      } else {
        year = restoSemBarra.substring(3);
      }

      let result = seq + '.';
      if (suffix) result += suffix;
      if (year || restoStr.includes('/')) result += '/' + year.substring(0, 4);
      return result;
    } 
    
    const partesBarra = limpo.split('/');
    seq = partesBarra[0].replace(/\D/g, '');
    year = partesBarra.slice(1).join('').replace(/\D/g, '');
    return `${seq}/${year.substring(0, 4)}`;
  }

  /**
   * Formato evento/protocolo simples: 00000/AAAA
   * Mantém pad de 5 zeros (padrão legado da diretiva original).
   */
  private formatarProtocolo(valor: string): string {
    const digits = valor.replace(/\D/g, '');
    if (!digits) return '';

    const temBarra = valor.includes('/');

    const pad5 = (n: string): string => n.padStart(5, '0');

    if (temBarra) {
      const partes = valor.split('/');
      const num  = pad5(partes[0].replace(/\D/g, '').substring(0, 5));
      const year = (partes[1] ?? '').replace(/\D/g, '').substring(0, 4);
      return `${num}/${year}`;
    }

    const soZeros = digits.split('').every(c => c === '0');
    if (soZeros) {
      return digits.length <= 4 ? digits : digits.substring(0, 4);
    }

    const semZeros = digits.replace(/^0+/, '');
    if (semZeros.length <= 5) return pad5(semZeros);

    const num  = pad5(semZeros.substring(0, 5));
    const year = semZeros.substring(5, 9);
    return `${num}/${year}`;
  }

  /**
   * Formato PTS: NN.NNNNN.NNNN (11 dígitos)
   */
  private formatarPTS(valor: string): string {
    const digits = valor.replace(/\D/g, '').substring(0, 11);
    let resultado = '';
    for (let i = 0; i < digits.length; i++) {
      if (i === 2 || i === 7) resultado += '.';
      resultado += digits[i];
    }
    return resultado;
  }
}
